/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {Text, VisuallyHidden} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useIntl} from 'react-intl'
import msg from '@salesforce/retail-react-app/app/components/display-price/messages'
import {useMultiStyleConfig} from '@chakra-ui/react'

/**
 * Component that displays current price of a product with a11y
 * @param price - price of the product
 * @param as - an HTML tag or component to be rendered as
 * @param isRange - show price as range or not
 * @param currency - currency to show the price in
 * @param labelForA11y - label to be used for a11y
 * @param isEditorialComponent - if true, hides the VisuallyHidden announcement for screen readers
 * @param extraProps - extra props to be passed into Text Component
 * @returns {JSX.Element}
 */
const CurrentPrice = ({
    price,
    as,
    isRange = false,
    currency,
    // keep for retrocompatibility: otherwise it will be passed to extraProps in the text component below
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    labelForA11y,
    variant = '',
    rangePriceProps,
    ...extraProps
}) => {
    const intl = useIntl()
    const currentPriceText = intl.formatNumber(price, {
        style: 'currency',
        currency,
    })
    const styles = useMultiStyleConfig('PriceCard', {variant})

    return (
        <>
            <VisuallyHidden suppressHydrationWarning>
                {isRange
                    ? intl.formatMessage(msg.ariaLabelCurrentPriceWithRange, {
                          currentPrice: currentPriceText,
                      })
                    : intl.formatMessage(msg.ariaLabelCurrentPrice, {
                          currentPrice: currentPriceText,
                      })}
            </VisuallyHidden>
            {isRange ? (
                <Text
                    {...styles.currentPrice}
                    as={as}
                    {...extraProps}
                    aria-label={intl.formatMessage(msg.ariaLabelCurrentPriceWithRange, {
                        currentPrice: currentPriceText,
                    })}
                    suppressHydrationWarning
                >
                    {intl.formatMessage(msg.currentPriceWithRange, {
                        currentPrice: rangePriceProps ? (
                            <Text as="span" {...rangePriceProps}>
                                {currentPriceText}
                            </Text>
                        ) : (
                            currentPriceText
                        ),
                    })}
                </Text>
            ) : (
                <Text
                    {...styles.currentPrice}
                    as={as}
                    {...extraProps}
                    aria-label={intl.formatMessage(msg.ariaLabelCurrentPrice, {
                        currentPrice: currentPriceText,
                    })}
                    suppressHydrationWarning
                >
                    {currentPriceText}
                </Text>
            )}
        </>
    )
}
CurrentPrice.propTypes = {
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    labelForA11y: PropTypes.string,
    as: PropTypes.string,
    isRange: PropTypes.bool,
    extraProps: PropTypes.object,
    variant: PropTypes.string,
    rangePriceProps: PropTypes.object,
}

export default CurrentPrice
